import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Gift, Users, DollarSign, RefreshCw, Loader2, Settings,
  Share2, Copy, CheckCircle, TrendingUp, Mail, Percent
} from 'lucide-react';

export default function CustomerReferralTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [settings, setSettings] = useState({
    is_enabled: true,
    referrer_reward: 20,
    referee_discount: 20,
    min_booking_value: 50,
    reward_type: 'fixed', // 'fixed' or 'percent'
    max_referrals_per_customer: 10
  });
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load customer referrals
      const referralsRes = await api.get('/admin/customer-referrals').catch(() => ({ data: [] }));
      setReferrals(referralsRes.data || []);
      
      // Load settings
      const settingsRes = await api.get('/admin/customer-referral-settings').catch(() => ({ data: settings }));
      if (settingsRes.data) {
        setSettings(prev => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/customer-referral-settings', settings);
      toast.success('Referral settings saved!');
      setShowSettingsDialog(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Stats
  const totalReferrals = referrals.length;
  const successfulReferrals = referrals.filter(r => r.status === 'completed').length;
  const totalRewardsGiven = referrals.filter(r => r.status === 'completed').reduce((sum, r) => sum + (r.referrer_reward || 0), 0);
  const conversionRate = totalReferrals > 0 ? ((successfulReferrals / totalReferrals) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-referral-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Gift className="h-6 w-6 text-pink-500" />
            Customer Referral Program
          </h2>
          <p className="text-muted-foreground">Give $20, Get $20 - Turn customers into ambassadors</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setShowSettingsDialog(true)} variant="outline" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </Button>
        </div>
      </div>

      {/* Program Status */}
      <Card className={`p-6 ${settings.is_enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${settings.is_enabled ? 'bg-green-500' : 'bg-gray-400'}`}>
              <Gift className="h-8 w-8 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{settings.is_enabled ? 'Program Active' : 'Program Inactive'}</h3>
              <p className="text-muted-foreground">
                {settings.reward_type === 'fixed' 
                  ? `Give $${settings.referee_discount}, Get $${settings.referrer_reward}`
                  : `Give ${settings.referee_discount}% off, Get ${settings.referrer_reward}% off`
                }
              </p>
            </div>
          </div>
          <Switch
            checked={settings.is_enabled}
            onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
          />
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Referrals</p>
              <p className="text-xl font-bold text-blue-600">{totalReferrals}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Successful</p>
              <p className="text-xl font-bold text-green-600">{successfulReferrals}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Conversion Rate</p>
              <p className="text-xl font-bold text-purple-600">{conversionRate}%</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
              <DollarSign className="h-5 w-5 text-pink-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rewards Given</p>
              <p className="text-xl font-bold text-pink-600">${totalRewardsGiven}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* How It Works */}
      <Card className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 border-pink-200">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Share2 className="h-5 w-5 text-pink-600" />
          How It Works (Customer View)
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold shrink-0">1</div>
            <div>
              <p className="font-medium">Share Your Code</p>
              <p className="text-sm text-muted-foreground">Each customer gets a unique referral code after booking</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold shrink-0">2</div>
            <div>
              <p className="font-medium">Friend Books</p>
              <p className="text-sm text-muted-foreground">Friend uses the code and gets ${settings.referee_discount} off</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold shrink-0">3</div>
            <div>
              <p className="font-medium">Both Get Rewarded</p>
              <p className="text-sm text-muted-foreground">Referrer gets ${settings.referrer_reward} credit</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-pink-600 text-white flex items-center justify-center font-bold shrink-0">4</div>
            <div>
              <p className="font-medium">Use Credits</p>
              <p className="text-sm text-muted-foreground">Credits applied to next booking automatically</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Recent Referrals */}
      <Card className="p-6">
        <h3 className="font-semibold text-lg mb-4">Recent Referrals</h3>
        {referrals.length === 0 ? (
          <div className="text-center py-12">
            <Gift className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
            <p className="text-lg font-medium text-muted-foreground">No referrals yet</p>
            <p className="text-sm text-muted-foreground">Referrals will appear here when customers share their codes</p>
          </div>
        ) : (
          <div className="space-y-3">
            {referrals.slice(0, 10).map((referral, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-medium">{referral.referrer_email}</p>
                    <p className="text-sm text-muted-foreground">
                      Referred: {referral.referee_email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge variant={referral.status === 'completed' ? 'default' : 'secondary'}>
                    {referral.status}
                  </Badge>
                  {referral.status === 'completed' && (
                    <span className="font-bold text-green-600">+${referral.referrer_reward}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Referral Program Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Program Enabled</Label>
                <p className="text-xs text-muted-foreground">Allow customers to refer friends</p>
              </div>
              <Switch
                checked={settings.is_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, is_enabled: checked })}
              />
            </div>
            
            <div>
              <Label>Reward Type</Label>
              <select
                value={settings.reward_type}
                onChange={(e) => setSettings({ ...settings, reward_type: e.target.value })}
                className="w-full border rounded-md p-2 mt-1"
              >
                <option value="fixed">Fixed Amount ($)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Referrer Reward</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={settings.referrer_reward}
                    onChange={(e) => setSettings({ ...settings, referrer_reward: parseInt(e.target.value) || 0 })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {settings.reward_type === 'fixed' ? '$' : '%'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">What referrer gets</p>
              </div>
              <div>
                <Label>Referee Discount</Label>
                <div className="relative">
                  <Input
                    type="number"
                    value={settings.referee_discount}
                    onChange={(e) => setSettings({ ...settings, referee_discount: parseInt(e.target.value) || 0 })}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {settings.reward_type === 'fixed' ? '$' : '%'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">What new customer gets</p>
              </div>
            </div>
            
            <div>
              <Label>Minimum Booking Value</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={settings.min_booking_value}
                  onChange={(e) => setSettings({ ...settings, min_booking_value: parseInt(e.target.value) || 0 })}
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Minimum booking to qualify for referral</p>
            </div>
            
            <div>
              <Label>Max Referrals Per Customer</Label>
              <Input
                type="number"
                value={settings.max_referrals_per_customer}
                onChange={(e) => setSettings({ ...settings, max_referrals_per_customer: parseInt(e.target.value) || 10 })}
              />
              <p className="text-xs text-muted-foreground mt-1">Prevent abuse</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
